import { isNullOrUndefined, Permission } from '@uzurv/core';
import { autoserialize, autoserializeAs, Deserialize } from 'cerialize';
import { UserSecurityDetails } from './../../models/user-security-details';

/**
 * @whatItDoes This class models an authenticated user's token and provides methods for retrieving tokenized information
 */

export class AuthData {
  @autoserialize
  token: string;

  @autoserializeAs(Permission)
  permissions: Array<Permission>;

  constructor(token: string) {
    this.token = token;
  }

  /**
   * Decodes our custom JWT style token. The two fields are split on '.' and
   * the first blob is converted from base64 and returned
   */
  getUserSecurityDetails(): UserSecurityDetails {
    if (isNullOrUndefined(this.token)) {
      throw new Error('token not found');
    }

    const splitToken = this.token.split('.')[0];
    return Deserialize(JSON.parse(atob(splitToken)), UserSecurityDetails);
  }

  /**
   * Returns the id parsed from the token
   */
  get id(): number {
    const userSecurityDetails: UserSecurityDetails = this.getUserSecurityDetails();
    return isNullOrUndefined(userSecurityDetails) ? null : userSecurityDetails.id;
  }

  /**
   * Returns the expiration date parsed from the token
   */
  get expires(): Date {
    const userSecurityDetails: UserSecurityDetails = this.getUserSecurityDetails();
    return isNullOrUndefined(userSecurityDetails) ? null : userSecurityDetails.expires;
  }

  /**
   * Returns the person id parsed from the token
   */
  get personId(): number {
    const userSecurityDetails: UserSecurityDetails = this.getUserSecurityDetails();
    return isNullOrUndefined(userSecurityDetails) ? null : userSecurityDetails.personId;
  }

  /**
   * Returns the username parsed from the token
   */
  get username(): string {
    const userSecurityDetails: UserSecurityDetails = this.getUserSecurityDetails();
    return isNullOrUndefined(userSecurityDetails) ? null : userSecurityDetails.username;
  }

  /**
   * Returns the granted authorities parsed from the token
   */
  get authorities(): Array<string> {
    const userSecurityDetails: UserSecurityDetails = this.getUserSecurityDetails();
    return isNullOrUndefined(userSecurityDetails) ? [] : userSecurityDetails.authorities;
  }
}
